<template>
  <div class="workshop-list">
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else class="mobile-button-container">
      <div class="mobile-button hide">
        <main-button depressed @click="goTo(routes.organizer.newWorkshop)">
          {{ adminText('workflowsAll.create_workshop_ba855ec', 'Create workshop') }}
        </main-button>
      </div>
      <v-layout wrap>
        <v-flex class="title-screen" xs12>
          <span>{{ adminText('workflowsAll.workshops_e28088b', 'Workshops') }}</span>
        </v-flex>
        <v-flex>
          <div class="mb-32 title-description">
            {{ adminText('workflowsAll.you_can_add_or_edit_workshops_hosted_at_your__01972de', 'You can add or edit workshops hosted at your event. You can also track current attendance quota uptake and manage attendees for every workshop.') }}
          </div>
        </v-flex>
        <v-flex v-if="!hasConferenceApp" class="pb-16" xs12>
          <v-layout align-start wrap>
            <main-button class="desktop-button" depressed @click="goTo(routes.organizer.newWorkshop)">
              {{ adminText('workflowsAll.create_workshop_ba855ec', 'Create workshop') }}
            </main-button>
            <div v-if="exceedsEventLimit" class="event-limit-banner">
              <v-icon size="24" color="#0277bd">error</v-icon>
              <span>
                {{ adminText('workflowsAll.capacity_exceeds_event_limit', 'Capacity exceeds event limit.') }}<br>
                {{ adminText('workflowsAll.increase_limit_or_reduce_capacity', 'Increase the limit or reduce capacity.') }}
              </span>
            </div>
          </v-layout>
        </v-flex>
        <v-flex v-else class="pb-16" xs12>
          <v-layout class="workshop-toolbar" align-center wrap>
            <div class="toolbar-filter">
              <select-field
                v-model="selectedHallId"
                :items="workflowUi(hallOptions)"
                item-render="name"
                item-select="id"
                :label="adminText('workflowsAll.select_a_hall_3f3689f', 'Select a Hall')"
                hide-details
              />
            </div>
            <div class="toolbar-filter">
              <select-field
                v-model="selectedDay"
                :items="workflowUi(dayOptions)"
                item-render="label"
                item-select="value"
                :label="adminText('workflowsAll.select_a_day_4fb18f9', 'Select a Day')"
                hide-details
              />
            </div>
            <v-menu v-model="createMenu" offset-y nudge-bottom="4">
              <div slot="activator" class="create-split">
                <v-btn depressed color="primary" class="create-split__main">{{ adminText('workflowsAll.create_6e157c5', 'Create') }}</v-btn>
                <v-btn depressed color="primary" class="create-split__caret">
                  <v-icon color="white">expand_more</v-icon>
                </v-btn>
              </div>
              <v-list>
                <v-list-tile @click="goTo(routes.organizer.newWorkshop)">
                  <v-list-tile-title>{{ adminText('workflowsAll.workshop_13b8fe3', 'Workshop') }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="openBreakDialog(null)">
                  <v-list-tile-title>{{ adminText('workflowsAll.break_7c9d6bf', 'Break') }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <div v-if="exceedsEventLimit" class="event-limit-banner">
              <v-icon size="24" color="#0277bd">error</v-icon>
              <span>
                {{ adminText('workflowsAll.capacity_exceeds_event_limit', 'Capacity exceeds event limit.') }}<br>
                {{ adminText('workflowsAll.increase_limit_or_reduce_capacity', 'Increase the limit or reduce capacity.') }}
              </span>
            </div>
          </v-layout>
        </v-flex>
        <v-flex xs12>
          <data-table
            :items="displayedRows"
            item-clickable
            :custom-sort="dateColumnSort"
            :sort-by="'from'"
            :table-row-style="{ borderBottom: '1px solid rgba(0,0,0,.12) !important' }"
            :sort-desc="false"
            :must-sort="true"
            :row-class="rowClass"
            :headers="workflowUi([
              { text: adminText('workflowsAll.name_709a232', 'Name'), align: 'left', value: 'name' },
              { text: adminText('workflowsAll.attendance_limit_25aa5e1', 'Attendance limit'), align: 'left', value: 'attendanceLimit' },
              { text: adminText('workflowsAll.start_date_ff99f5b', 'Start date'), align: 'left', value: 'from' },
              { text: adminText('workflowsAll.end_date_89d10cd', 'End date'), align: 'left', value: 'to' },
            ])"
            :rows-per-page-items="[10, 25, 50, 100]"
            @item-click="onRowClick"
          />
        </v-flex>
      </v-layout>
      <break-dialog
        v-if="hasConferenceApp"
        :show.sync="showBreakDialog"
        :event-id="eventId"
        :halls="halls"
        :day="breakDay"
        :event-from="event.from"
        :event-to="event.to"
        :default-hall-id="breakHallId"
        :break-item="editedBreak"
        @saved="refetchWorkshops"
        @delete="confirmDeleteBreak"
      />
      <break-delete-dialog
        :show.sync="showDeleteBreakDialog"
        :break-item="breakToDelete"
        :on-delete="deleteBreak"
      />
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import DataTable from '../shared/data-table'
import Spinner from '../shared/spinner'
import BreakDialog from './break-dialog.vue'
import BreakDeleteDialog from './break-delete-dialog.vue'
import { GET_EVENT_WORKSHOPS } from '../../graphql/workshop.graphql.js'
import { GET_EVENT_SETTINGS } from '../../graphql/event.graphql'
import { GET_EVENT_HALLS } from '../../graphql/halls.graphql'
import { deleteWorkshop } from '../../http'
import moment from 'moment'

const DAY_FORMAT = 'YYYY-MM-DD'
// v-select does not handle a null item value well, so the "all" options get their own sentinel
const ALL_HALLS = 'ALL'
const ALL_DAYS = 'ALL'

export default {
  mixins: [WorkflowDisplay],
  name: 'workshop-list-container',
  components: {
    Spinner,
    DataTable,
    BreakDialog,
    BreakDeleteDialog,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      event: {},
      halls: [],
      workshops: [],
      // query data as returned, before attendanceLimit is replaced with display text
      rawWorkshops: [],
      selectedHallId: ALL_HALLS,
      selectedDay: ALL_DAYS,
      createMenu: false,
      showBreakDialog: false,
      showDeleteBreakDialog: false,
      editedBreak: null,
      breakToDelete: null,
    }
  },
  computed: {
    displayedRows() {
      return this.visibleRows.map(item => ({ ...item, from: this.workflowDate(item.from, 'LL, H:mm'), to: this.workflowDate(item.to, 'LL, H:mm') }))
    },
    exceedsEventLimit() {
      const { attendeeLimit, extraModules } = this.event
      if (!attendeeLimit || (extraModules && extraModules.ticketing)) return false

      return this.rawWorkshops.some(
        w => !w.isBreak && !w.unlimitedAttendance && w.attendanceLimit > attendeeLimit,
      )
    },
    hasConferenceApp() {
      return Boolean(this.event.extraModules && this.event.extraModules.conferenceMobileApp)
    },
    hallOptions() {
      return [{ id: ALL_HALLS, name: 'workflowsAll.all_halls_6a9fe6f' }, ...this.halls]
    },
    // a break created while "All halls" is filtered defaults to applying to all halls
    breakHallId() {
      return this.selectedHallId === ALL_HALLS ? null : this.selectedHallId
    },
    // prefill the dialog's day from the filter; with no day filtered the user picks one
    breakDay() {
      return this.selectedDay === ALL_DAYS ? null : this.selectedDay
    },
    // every calendar day the event spans, preceded by the unfiltered default
    dayOptions() {
      const days = [{ value: ALL_DAYS, label: 'workflowsAll.all_days_b4f794c' }]
      if (!this.event.from || !this.event.to) return days

      const last = moment(this.event.to).startOf('day')
      const cursor = moment(this.event.from).startOf('day')

      while (cursor.isSameOrBefore(last)) {
        days.push({ value: cursor.format(DAY_FORMAT), label: this.workflowDate(cursor, 'LL') })
        cursor.add(1, 'day')
      }

      return days
    },
    visibleRows() {
      if (!this.hasConferenceApp) return this.workshops.filter(w => !w.isBreak)

      return this.workshops.filter(w => {
        const dayMatches = this.selectedDay === ALL_DAYS || w.day === this.selectedDay

        return dayMatches && this.matchesHall(w)
      })
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_SETTINGS,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          const { getEvent: event } = data
          this.event = event
        }
      },
    },
    getHalls: {
      query: GET_EVENT_HALLS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          const { getHalls } = data
          this.halls = getHalls
        }
      },
    },
    getEventWorkshops: {
      query: GET_EVENT_WORKSHOPS,
      variables() {
        return {
          eventId: this.eventId,
          // Only a conference event has breaks; `visibleRows` still filters them out
          // for the others in case the event loads after the workshops.
          includeBreaks: this.hasConferenceApp,
        }
      },
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          const { getWorkshops: workshops } = data
          this.rawWorkshops = workshops
          this.workshops = workshops.map(w => {
            // breaks have no attendance, so the column stays empty for them
            const attendanceLimit = w.isBreak
              ? ''
              : w.unlimitedAttendance
              ? 'Unlimited'
              : w.attendanceLimit

            return {
              ...w,
              // keep raw values for proper sorting
              fromRaw: w.from,
              toRaw: w.to,
              day: moment(w.from).format(DAY_FORMAT),
              // formatted for display
              to: w.to,
              from: w.from,
              attendanceLimit,
            }
          })
        }
      },
    },
  },
  methods: {
    // A break with no hall applies to all halls, so it shows whichever hall is filtered.
    // A hall-less workshop (data from before halls were required) only shows under "All halls".
    matchesHall(row) {
      if (this.selectedHallId === ALL_HALLS) return true
      if (row.hall) return row.hall.id === this.selectedHallId

      // a hall-less break applies to every hall, so it shows under each
      return Boolean(row.isBreak)
    },
    // conference events render workshops as blue links and breaks as plain rows
    rowClass(item) {
      if (!this.hasConferenceApp) return ''

      return item.isBreak ? 'break-row' : 'workshop-row'
    },
    onRowClick(item) {
      if (item.isBreak) {
        this.openBreakDialog(item)

        return
      }

      this.goTo(this.routes.organizer.updateWorkshop, {
        eventId: this.eventId,
        workshopId: item.id,
      })
    },
    openBreakDialog(breakItem) {
      this.editedBreak = breakItem
      this.showBreakDialog = true
    },
    confirmDeleteBreak(breakItem) {
      this.breakToDelete = breakItem
      this.showBreakDialog = false
      this.showDeleteBreakDialog = true
    },
    async deleteBreak() {
      await deleteWorkshop(this.eventId, this.breakToDelete.id)
      this.breakToDelete = null
      await this.refetchWorkshops()
    },
    refetchWorkshops() {
      return this.$apollo.queries.getEventWorkshops.refetch()
    },
    dateColumnSort(items, sortBy, isDesc) {
      const field = Array.isArray(sortBy) ? sortBy[0] : sortBy
      const desc = Array.isArray(isDesc) ? isDesc[0] : isDesc
      if (!field) return items

      const useDate = field === 'from' || field === 'to'

      return [...items].sort((a, b) => {
        if (useDate) {
          const aRaw = a[`${field}Raw`] || a[field]
          const bRaw = b[`${field}Raw`] || b[field]
          const diff = moment(aRaw).diff(moment(bRaw))

          return desc ? -diff : diff
        }

        const aVal = a[field]
        const bVal = b[field]
        const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0

        return desc ? -cmp : cmp
      })
    },
  },
}
</script>

<style>
/*
  Unscoped on purpose: the row classes land on <tr>s rendered by data-table, which a
  scoped style cannot reach. Everything is nested under .workshop-list instead so
  nothing leaks to other screens.
*/
/* the toolbar wraps on narrow screens, so every child carries its own bottom gap */
.workshop-list .workshop-toolbar > * {
  margin: 0 16px 16px 0;
}
.workshop-list .workshop-toolbar > .toolbar-filter {
  max-width: 200px;
}
@media (max-width: 800px) {
  .workshop-list .workshop-toolbar > .toolbar-filter {
    max-width: none;
    width: 100%;
    margin-right: 0;
  }
}
/* pushed to the right end of the toolbar row */
.workshop-list .event-limit-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-left: auto;
  margin-right: 0;
  padding: 10px;
  border-radius: 8px;
  background: #e6f1f8;
  color: #0277bd;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  line-height: 1.5;
}
/* split "Create" CTA: two primary buttons sharing one pill, separated by a 2px gap */
.workshop-list .create-split {
  display: inline-flex;
  gap: 2px;
  border-radius: 24px;
  overflow: hidden;
}
.workshop-list .create-split .v-btn {
  margin: 0;
  height: 48px;
  min-width: 0;
  border-radius: 0;
}
.workshop-list .create-split__main {
  padding: 0 24px;
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 18px;
  letter-spacing: -0.27px;
}
.workshop-list .create-split__caret {
  width: 48px;
  padding: 0;
}
.workshop-list .workshop-row .data-table-text {
  color: #0277bd;
}
.workshop-list .workshop-row {
  background-color: rgba(2, 119, 189, 0.04);
}
.workshop-list .break-row .data-table-text {
  color: #333333;
}
</style>
